import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, onSnapshot, doc, updateDoc, addDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useUserRole } from '../../hooks/useUserRole';
import { PlusCircle, Pin, AlertCircle } from 'lucide-react';
import { NewsModal } from './NewsModal';
import { CreateNewsModal } from './CreateNewsModal';

interface News {
  id: string;
  title: string;
  content: string;
  createdAt: Date;
  updatedAt?: Date;
  author: {
    id: string;
    name: string;
  };
  important?: boolean;
  pinned?: boolean;
}

const stripHtml = (html: string, maxLength = 300) => {
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  const text = tmp.textContent || tmp.innerText || '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength).trim() + '...';
};

export const DashboardHome: React.FC = () => {
  const { user } = useAuth();
  const { isManager } = useUserRole();
  const [news, setNews] = useState<News[]>([]);
  const [selectedNews, setSelectedNews] = useState<News | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingNews, setEditingNews] = useState<News | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const q = query(
      collection(db, 'news'),
      orderBy('pinned', 'desc'),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const newsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate(),
          updatedAt: doc.data().updatedAt?.toDate()
        })) as News[];
        setNews(newsData);
        setError(null);
      },
      (err) => {
        console.error('Error loading news:', err);
        setError('Neuigkeiten konnten nicht geladen werden');
      }
    );

    return () => unsubscribe();
  }, []);

  const handleCreateNews = async (newsData: Omit<News, 'id' | 'createdAt' | 'author'>) => {
    try {
      if (!user) return;

      await addDoc(collection(db, 'news'), {
        ...newsData,
        createdAt: new Date(),
        author: {
          id: user.uid,
          name: user.displayName || user.email
        }
      });

      setIsCreateModalOpen(false);
    } catch (error) {
      console.error('Error creating news:', error);
      throw new Error('Neuigkeit konnte nicht erstellt werden');
    }
  };

  const handleUpdateNews = async (newsId: string, updates: Partial<News>) => {
    try {
      const newsRef = doc(db, 'news', newsId);
      await updateDoc(newsRef, {
        ...updates,
        updatedAt: new Date()
      });
      setEditingNews(null);
    } catch (error) {
      console.error('Error updating news:', error);
      throw new Error('Neuigkeit konnte nicht aktualisiert werden');
    }
  };

  const handleDeleteNews = async (newsId: string) => {
    try {
      await deleteDoc(doc(db, 'news', newsId));
    } catch (error) {
      console.error('Error deleting news:', error);
      throw new Error('Neuigkeit konnte nicht gelöscht werden');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Neuigkeiten</h2>
        {isManager && (
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600"
          >
            <PlusCircle className="h-5 w-5 mr-2" />
            Neue Meldung
          </button>
        )}
      </div>

      {error && (
        <div className="rounded-md bg-red-50 dark:bg-red-900/50 p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400 dark:text-red-300" />
            <div className="ml-3">
              <p className="text-sm text-red-700 dark:text-red-200">{error}</p>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {news.map((item) => (
          <div
            key={item.id}
            className={`
              bg-white dark:bg-gray-800 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 cursor-pointer
              ${item.important ? 'border-l-4 border-red-500 dark:border-red-400' : ''}
              ${item.pinned ? 'border-l-4 border-indigo-500 dark:border-indigo-400' : ''}
            `}
            onClick={() => setSelectedNews(item)}
          >
            <div className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                      {item.title}
                    </h3>
                    <span className="text-lg text-gray-500 dark:text-gray-400">
                      {item.createdAt.toLocaleDateString('de-DE', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                      })}
                    </span>
                  </div>
                  <div className="mt-1 flex items-center space-x-2">
                    {item.important && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                        Wichtig
                      </span>
                    )}
                    {item.pinned && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200">
                        <Pin className="h-3 w-3 mr-1" />
                        Angepinnt
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                {stripHtml(item.content)}
              </p>
            </div>
          </div>
        ))}
      </div>

      <NewsModal
        isOpen={!!selectedNews}
        onClose={() => setSelectedNews(null)}
        news={selectedNews}
        onEdit={isManager ? setEditingNews : undefined}
        onDelete={isManager ? (news) => {
          setSelectedNews(null);
          handleDeleteNews(news.id);
        } : undefined}
      />

      <CreateNewsModal
        isOpen={isCreateModalOpen || !!editingNews}
        onClose={() => {
          setIsCreateModalOpen(false);
          setEditingNews(null);
        }}
        onSubmit={editingNews ? 
          (data) => handleUpdateNews(editingNews.id, data) : 
          handleCreateNews
        }
        initialData={editingNews}
        onDelete={editingNews ? () => handleDeleteNews(editingNews.id) : undefined}
      />
    </div>
  );
};
